/**
 * QueuePage — reusable Type 1 Standard AG Grid page shell.
 * Used by Returns and Transfers sub-pages to avoid duplication.
 */
import { useState, useCallback, useRef, useMemo, useEffect, type JSX, type ReactNode } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ModuleRegistry,
  themeQuartz,
  type ColDef,
  type GridApi,
  type GridReadyEvent,
  type IGetRowsParams,
} from 'ag-grid-community';
import { Search, type LucideIcon } from 'lucide-react';
import clsx from 'clsx';
import { supabase } from '../../lib/supabase';
import { SELECTION_COLUMN_DEF, emptyFormatter } from '../../lib/gridUtils';
import { reportDatasourceFailure } from '../../lib/agGridErrorTracker';
import ExportButton from './ExportButton';

ModuleRegistry.registerModules([AllCommunityModule]);

// supabase.rpc's first argument is typed as a specific union of known function
// names from the generated Database types. QueuePage is a reusable shell that
// takes the RPC name as a string prop, so we widen to a generic signature here.
type GenericSupabaseRpc = (
  name: string,
  args: Record<string, unknown>
) => PromiseLike<{
  data: unknown;
  error: { message: string } | null;
}>;

const callRpc = supabase.rpc.bind(supabase) as unknown as GenericSupabaseRpc;

interface StatusTab {
  key: string;
  label: string;
}

interface QueuePageProps {
  title: string;
  rpcName: string;
  rpcParams?: Record<string, unknown>;
  columns: ColDef[];
  statusTabs?: StatusTab[];
  emptyIcon?: LucideIcon;
  emptyMessage: string;
  actionButton?: ReactNode;
  searchPlaceholder?: string;
  /**
   * Day 64.5 V-ROUI: when true, hides the row-selection checkbox column.
   * Consumers pass `readOnly={isViewer || isDistRep}` (or the appropriate
   * read-only-role boolean) to disable bulk-action affordances for read-only
   * roles. ExportButton + search + status tabs + sorting all stay functional
   * (read operations, allowed under V-ROUI per the gating taxonomy).
   */
  readOnly?: boolean;
  /**
   * Cluster 8 — optional row-click handler invoked with the row data. Returns queues use it to
   * open the disposition modal. The SKU cell's own onClick calls stopPropagation, so in-cell
   * deep-links still take precedence over the row click.
   */
  onRowClicked?: (data: unknown) => void;
  /**
   * Cluster 8 — change this to force the infinite datasource to re-fetch from offset 0 (e.g.
   * after a mutation). Any new value resets the datasource.
   */
  refreshSignal?: unknown;
}

export default function QueuePage({
  title,
  rpcName,
  rpcParams = {},
  columns,
  statusTabs = [],
  emptyIcon: EmptyIcon,
  emptyMessage,
  actionButton,
  searchPlaceholder = 'Search...',
  readOnly = false,
  onRowClicked,
  refreshSignal,
}: QueuePageProps): JSX.Element {
  const gridRef = useRef<AgGridReact | null>(null);
  const gridApiRef = useRef<GridApi | null>(null);
  const [searchText, setSearchText] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [totalCount, setTotalCount] = useState<number>(0);

  const stateRef = useRef<{ searchText: string; statusFilter: string }>({
    searchText,
    statusFilter,
  });
  stateRef.current = { searchText, statusFilter };

  const getRowsFn = useCallback(
    async (params: IGetRowsParams): Promise<void> => {
      const { startRow, endRow, successCallback, failCallback } = params;
      const { searchText: st, statusFilter: sf } = stateRef.current;
      try {
        const { data, error } = await callRpc(rpcName, {
          ...rpcParams,
          p_status: sf === 'all' ? null : sf,
          p_search: st.trim() || null,
          p_limit: endRow - startRow,
          p_offset: startRow,
        });
        if (error) throw new Error(error.message);
        const rows = (data as unknown[]) || [];
        if (startRow === 0) setTotalCount(rows.length);
        successCallback(
          rows,
          startRow === 0
            ? rows.length < endRow - startRow
              ? rows.length
              : rows.length + 100
            : totalCount
        );
      } catch (e) {
        reportDatasourceFailure(title, { rpcName, params: { startRow, endRow } }, e, () =>
          failCallback()
        );
      }
    },
    [rpcName, rpcParams, title, totalCount]
  );

  const onGridReady = useCallback(
    (event: GridReadyEvent): void => {
      gridApiRef.current = event.api;
      event.api.setGridOption('datasource', { getRows: getRowsFn });
    },
    [getRowsFn]
  );

  useEffect(() => {
    if (gridApiRef.current) gridApiRef.current.setGridOption('datasource', { getRows: getRowsFn });
  }, [searchText, statusFilter, getRowsFn, refreshSignal]);

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      resizable: true,
      sortingOrder: ['asc', 'desc', null],
      unSortIcon: true,
      headerClass: 'ag-left-aligned-header',
      cellStyle: { textAlign: 'left' },
      valueFormatter: emptyFormatter,
    }),
    []
  );

  const gridTheme = useMemo(
    () =>
      themeQuartz.withParams({
        backgroundColor: 'var(--surface-0)',
        headerBackgroundColor: 'var(--surface-1)',
        headerTextColor: 'var(--ink-muted)',
        foregroundColor: 'var(--ink-primary)',
        borderColor: 'var(--border)',
        rowHoverColor: 'var(--surface-3)',
        headerFontSize: 10,
        fontSize: 12,
        spacing: 4,
      }),
    []
  );

  return (
    <div className="flex flex-col h-full -m-4">
      <div className="flex-shrink-0 px-4 py-2 bg-surface-1 border-b border-border">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <h1 className="text-base font-semibold text-ink-bright">{title}</h1>
            {totalCount > 0 && <span className="text-xs text-ink-muted">{totalCount}</span>}
          </div>
          <div className="flex items-center gap-2">
            <ExportButton
              gridApiRef={gridApiRef}
              fileName={`arbi-${title.toLowerCase().replace(/\s+/g, '-')}`}
            />
            {actionButton}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {statusTabs.length > 0 && (
            <div className="flex items-center bg-surface-2 rounded-md p-0.5 gap-0.5 border border-border-subtle">
              {statusTabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setStatusFilter(tab.key)}
                  className={clsx(
                    'px-2 py-1 rounded text-2xs font-medium whitespace-nowrap transition-colors cursor-pointer',
                    statusFilter === tab.key
                      ? 'bg-accent/15 text-accent'
                      : 'text-ink-muted hover:text-ink-secondary'
                  )}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}
          <div className="relative w-60 ml-auto">
            <Search
              size={12}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted"
            />
            <input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder={searchPlaceholder}
              className="arbi-input pl-8 pr-3 py-1 text-xs w-full"
            />
          </div>
        </div>
      </div>
      <div className="flex-1 p-2" style={{ minHeight: 0 }}>
        <AgGridReact
          ref={gridRef}
          theme={gridTheme}
          columnDefs={columns}
          defaultColDef={defaultColDef}
          rowModelType="infinite"
          cacheBlockSize={100}
          maxBlocksInCache={50}
          selectionColumnDef={SELECTION_COLUMN_DEF}
          rowSelection={{
            mode: 'multiRow',
            enableClickSelection: false,
            checkboxes: !readOnly,
            headerCheckbox: false,
          }}
          onGridReady={onGridReady}
          onRowClicked={(e) => onRowClicked?.(e.data)}
          animateRows={false}
          headerHeight={36}
          rowHeight={42}
          suppressCellFocus={true}
          suppressHorizontalScroll={true}
          noRowsOverlayComponent={() => (
            <div className="flex flex-col items-center justify-center h-full">
              {EmptyIcon && <EmptyIcon size={48} className="text-ink-muted mb-3" />}
              <p className="text-sm text-ink-muted">
                {statusFilter !== 'all' || searchText
                  ? 'No items match your filters'
                  : emptyMessage}
              </p>
            </div>
          )}
        />
      </div>
    </div>
  );
}
